import type { ComponentProps } from 'react';
import { cn } from './cn';
import { focusRing } from './focus';

const SIZES = {
  /** A disc in a 28 px button (the text colours of the format bar). */
  sm: { button: 'h-7 min-w-7 rounded hover:bg-surface-2', disc: 'size-3.5' },
  /** The disc is the button (object colours, section pairs). */
  md: { button: 'size-7 rounded-full transition-transform duration-(--duration-fast) hover:scale-110', disc: 'size-full' },
} as const;

/**
 * A colour to pick, named by `label` (the colour's name), pressed as an accent ring; `edge` draws an inner ring (the
 * pill colour of a section pair). A press never takes the focus from the edited text (P5b: colouring while typing).
 */
export function ColorSwatch({
  color,
  label,
  pressed,
  edge,
  size = 'md',
  className,
  onMouseDown,
  ...rest
}: Omit<ComponentProps<'button'>, 'color' | 'children'> & { color: string; label: string; pressed?: boolean; edge?: string; size?: keyof typeof SIZES }) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      title={label}
      onMouseDown={(e) => {
        e.preventDefault();
        onMouseDown?.(e);
      }}
      className={cn(
        'inline-flex shrink-0 items-center justify-center aria-pressed:ring-2 aria-pressed:ring-accent aria-pressed:ring-offset-2 aria-pressed:ring-offset-surface pointer-coarse:size-11',
        focusRing,
        SIZES[size].button,
        className,
      )}
      {...rest}
    >
      <span data-swatch="" aria-hidden="true" className={cn('rounded-full ring-1 ring-fg/15', SIZES[size].disc)} style={{ background: color, ...(edge ? { boxShadow: `inset 0 0 0 3px ${edge}` } : {}) }} />
    </button>
  );
}
